import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Bell,
  Bookmark,
  Calendar,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Flag,
  Home,
  MapPin,
  Navigation,
  Newspaper,
  Phone,
  RefreshCw,
  Search,
  Share2,
  Star,
  Store,
  Tag,
  Volume2,
  Wrench,
  X,
} from 'lucide-react';
import './index.css';

const BRAND = '#153304';
const ORANGE = '#F97316';
const CLIENT_KEY = 'pm_client_id';
const CONTACT_EMAIL = 'contact@pulsify.me';
const AUTH_KEY = 'pulsifyme_auth_user';
const LOCATION_KEY = 'pulsifyme_location_state';
const LOCATION_INITIALIZED_KEY = 'pulsifyme_location_initialized';
const LOCATION_PERMISSION_KEY = 'pulsifyme_location_permission_status';
const LOCATION_COUNTRY_KEY = 'pulsifyme_country';
const LOCATION_CITY_KEY = 'pulsifyme_selected_city';
const LOCATION_MODE_KEY = 'pulsifyme_place_mode';
const LOCATION_LAT_KEY = 'pulsifyme_lat';
const LOCATION_LNG_KEY = 'pulsifyme_lng';
const FEEDBACK_DRAFT_KEY = 'pulsifyme_beta_feedback_v2_draft';
const SAVED_NEWS_KEY = 'pulsifyme_saved_news';
const LOGO = '/assets/branding/pulsifyme-logo.png';
const PULSE_ICON = '/assets/branding/guest-login-icon.png';
const AI_ICON = '/assets/branding/ai-summary-icon.png';

function getApiBaseUrl() {
  const configured = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001').replace(/\/+$/, '');
  return configured.endsWith('/api') ? configured : `${configured}/api`;
}

const API_BASE_URL = getApiBaseUrl();

const NZ_CITIES = [
  { slug: 'auckland', name: 'Auckland', lat: -36.8485, lng: 174.7633 },
  { slug: 'wellington', name: 'Wellington', lat: -41.2865, lng: 174.7762 },
  { slug: 'christchurch', name: 'Christchurch', lat: -43.5321, lng: 172.6362 },
  { slug: 'hamilton', name: 'Hamilton', lat: -37.787, lng: 175.2793 },
  { slug: 'tauranga', name: 'Tauranga', lat: -37.6878, lng: 176.1651 },
  { slug: 'dunedin', name: 'Dunedin', lat: -45.8788, lng: 170.5028 },
  { slug: 'queenstown', name: 'Queenstown', lat: -45.0312, lng: 168.6626 },
  { slug: 'rotorua', name: 'Rotorua', lat: -38.1368, lng: 176.2497 },
];

const AU_CITIES = [
  { slug: 'sydney', name: 'Sydney', lat: -33.8688, lng: 151.2093 },
  { slug: 'melbourne', name: 'Melbourne', lat: -37.8136, lng: 144.9631 },
  { slug: 'brisbane', name: 'Brisbane', lat: -27.4698, lng: 153.0251 },
  { slug: 'perth', name: 'Perth', lat: -31.9505, lng: 115.8605 },
  { slug: 'adelaide', name: 'Adelaide', lat: -34.9285, lng: 138.6007 },
  { slug: 'canberra', name: 'Canberra', lat: -35.2809, lng: 149.13 },
  { slug: 'gold-coast', name: 'Gold Coast', lat: -28.0167, lng: 153.4 },
  { slug: 'hobart', name: 'Hobart', lat: -42.8821, lng: 147.3272 },
];

const CITY_MAP = { NZ: NZ_CITIES, AU: AU_CITIES };
const DEFAULT_CITY = { NZ: 'auckland', AU: 'sydney' };

const NEWS_CATEGORIES = [
  { label: 'All', value: '' },
  { label: 'Politics', value: 'politics' },
  { label: 'Business', value: 'business' },
  { label: 'Finance', value: 'finance' },
  { label: 'Sports', value: 'sports' },
  { label: 'World', value: 'international' },
  { label: 'Technology', value: 'technology' },
  { label: 'Health', value: 'health' },
  { label: 'Weather', value: 'weather' },
];

const NEARBY_CATEGORIES = [
  { value: 'food_drink', label: 'Food & Drink', icon: '☕', image: 'nearby-restaurant.jpg', queryCategories: ['cafe', 'restaurant', 'bakery', 'fast_food', 'bar'] },
  { value: 'essentials', label: 'Essentials', icon: '🛒', image: 'nearby-grocery.jpg', queryCategories: ['supermarket', 'store', 'atm', 'laundry'] },
  { value: 'health', label: 'Health', icon: '💊', image: 'nearby-pharmacy.jpg', queryCategories: ['pharmacy', 'hospital', 'dentist', 'beauty_salon'] },
  { value: 'fuel', label: 'Fuel', icon: '⛽', image: 'nearby-services.jpg', queryCategories: ['gas_station'] },
  { value: 'parking', label: 'Parking', icon: '🅿️', image: 'nearby-default.jpg', queryCategories: ['parking'] },
  { value: 'fitness', label: 'Fitness', icon: '💪', image: 'nearby-fitness.jpg', queryCategories: ['gym'] },
  { value: 'parks_play', label: 'Parks & Play', icon: '🌳', image: 'nearby-kids.jpg', queryCategories: ['park', 'playground'] },
  { value: 'stay', label: 'Stay', icon: '🏨', image: 'nearby-shopping.jpg', queryCategories: ['lodging'] },
  { value: 'more', label: 'More', icon: '⋯', image: 'nearby-default.jpg', queryCategories: ['store'] },
];

const SERVICE_CATEGORIES = [
  { value: 'plumber', label: 'Plumber' },
  { value: 'electrician', label: 'Electrician' },
  { value: 'car_wash', label: 'Car Wash' },
  { value: 'locksmith', label: 'Locksmith' },
  { value: 'painter', label: 'Painter' },
  { value: 'roofing_contractor', label: 'Roofing' },
  { value: 'moving_company', label: 'Movers' },
  { value: 'car_repair', label: 'Auto Repair' },
  { value: 'home_services', label: 'More' },
];

const EVENT_CATEGORIES = ['All', 'Music', 'Sports', 'Arts', 'Family', 'Food'];
const EVENT_TIME_FILTERS = ['All', 'Today', 'Weekend', 'This Week'];
const SEARCH_HINTS = ['Search "coffee with parking"', 'Try "eggless cakes near me"', 'Find "South Indian food"', 'Search "kids play area"', 'Try "best Chinese restaurants"'];
const SEARCH_CHIPS = ['cafe', 'restaurants', 'parks', 'eggless cakes', 'south indian food', 'indoor play', 'open now'];

const PREVIEW_TICKER = [
  { symbol: 'NZX', price: 12481.34, change: 55.14 },
  { symbol: 'AIR', price: 0.615, change: -0.005 },
  { symbol: 'FPH', price: 26.8, change: 0.35 },
  { symbol: 'ATM', price: 13.45, change: -0.28 },
  { symbol: 'XJO', price: 7812.3, change: -22.5 },
  { symbol: 'CBA', price: 183.38, change: 0.85 },
  { symbol: 'BHP', price: 39.45, change: 0.62 },
];

const DUMMY_OFFERS = [
  { id: 'nz-001', title: '20% off your first online order', image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800&q=60', country: 'NZ', merchant_name: 'FreshMart NZ', description: 'Enjoy 20% off your first online grocery order.', offer_details: 'Use code FRESH20 at checkout.', valid_from: '2026-03-01', valid_to: '2026-07-31', is_active: true },
  { id: 'nz-002', title: 'Free coffee with any meal', image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=800&q=60', country: 'NZ', city: 'auckland', merchant_name: 'Brix & Co Cafe', description: 'Get a free flat white or long black with any main meal.', offer_details: 'Valid Monday-Friday. Dine-in only.', valid_from: '2026-03-15', valid_to: '2026-07-31', is_active: true },
  { id: 'nz-003', title: 'Buy 1 get 1 free pizza', image: 'https://images.unsplash.com/photo-1513104890138-7c749659a591?w=800&q=60', country: 'NZ', city: 'wellington', merchant_name: 'Ember Pizzeria', description: 'Double the pizza for the price of one every Tuesday.', offer_details: 'Equal or lesser value item free.', valid_from: '2026-03-01', valid_to: '2026-07-31', is_active: true },
  { id: 'au-001', title: '$10 off entertainment tickets', image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=800&q=60', country: 'AU', merchant_name: 'EventHub Australia', description: 'Save $10 on any event ticket booked through EventHub.', offer_details: 'Use code PULSE10 at checkout.', valid_from: '2026-03-01', valid_to: '2026-07-31', is_active: true },
  { id: 'au-002', title: 'Half-price Sydney Harbour ferry pass', image: 'https://images.unsplash.com/photo-1524293581917-878a6d017c71?w=800&q=60', country: 'AU', city: 'sydney', merchant_name: 'Sydney Ferries', description: 'Explore Sydney Harbour for half the price this weekend.', offer_details: 'Valid weekends only.', valid_from: '2026-03-01', valid_to: '2026-07-31', is_active: true },
  { id: 'au-003', title: '25% off rooftop dining', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=60', country: 'AU', city: 'melbourne', merchant_name: 'Altitude Melbourne', description: 'Dine above the Melbourne skyline and save 25%.', offer_details: 'Use code ROOF25.', valid_from: '2026-03-10', valid_to: '2026-07-31', is_active: true },
];

const FEEDBACK_STEPS = [
  ['overall_experience', 'Overall App Experience', 'How did PulsifyMe feel overall?'],
  ['news_quality', 'News Quality & Relevance', 'Were the stories useful, local and relevant?'],
  ['problem_value', 'Problem Solving Value', 'What problem did the app solve for you?'],
  ['ui_navigation', 'UI & Navigation', 'How easy was it to move around the app?'],
  ['performance', 'Performance & Loading', 'Did anything feel slow or broken?'],
  ['nearby_services_events', 'Nearby, Services & Events', 'How useful were local places, services and events?'],
  ['ai_features', 'AI Features & Summaries', 'How did the AI summaries and features feel?'],
  ['bugs', 'Bugs or Technical Problems', 'Tell us about any bugs or technical issues.'],
  ['disliked', 'Most Disliked Feature', 'What did you dislike most?'],
  ['recommendations', 'Recommendations / Suggestions', 'What should we improve next?'],
];

function generateUUID() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `pm-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getClientId() {
  let id = localStorage.getItem(CLIENT_KEY);
  if (!id) {
    id = generateUUID();
    localStorage.setItem(CLIENT_KEY, id);
  }
  return id;
}

const CLIENT_ID = getClientId();

function apiUrl(path) {
  return `${API_BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiGet(path, fallback) {
  const res = await fetch(apiUrl(path));
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  const data = await res.json();
  return data ?? fallback;
}

function clean(value, fallback = '') {
  const raw = value === undefined || value === null ? '' : String(value);
  const textarea = document.createElement('textarea');
  textarea.innerHTML = raw;
  return (textarea.value || raw).replace(/\s+/g, ' ').trim() || fallback;
}

function pick(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function cityFor(location) {
  return CITY_MAP[location.country].find((city) => city.slug === location.selectedCity) || CITY_MAP[location.country][0];
}

function countryName(country) {
  return country === 'AU' ? 'Australia' : 'New Zealand';
}

function countryFlag(country) {
  return country === 'AU' ? '🇦🇺' : '🇳🇿';
}

function readJson(key, fallback) {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function inferCountryFromCoords(lat, lng) {
  if (lat >= -48 && lat <= -33 && lng >= 165 && lng <= 180) return 'NZ';
  if (lat >= -44 && lat <= -10 && lng >= 112 && lng <= 154) return 'AU';
  return '';
}

function persistLocation(next) {
  localStorage.setItem(LOCATION_KEY, JSON.stringify(next));
  localStorage.setItem(LOCATION_COUNTRY_KEY, next.country);
  localStorage.setItem(LOCATION_CITY_KEY, next.selectedCity);
  localStorage.setItem(LOCATION_MODE_KEY, next.placeMode);
  if (next.gps) {
    localStorage.setItem(LOCATION_LAT_KEY, String(next.gps.lat));
    localStorage.setItem(LOCATION_LNG_KEY, String(next.gps.lng));
  } else {
    localStorage.removeItem(LOCATION_LAT_KEY);
    localStorage.removeItem(LOCATION_LNG_KEY);
  }
}

function articleKey(article) {
  return clean(pick(article.article_id, article.dedupe_key, article.url, article.id, article.title), generateUUID());
}

function articleMatchesSelected(article, selectedArticleId) {
  const selected = selectedArticleId || '';
  const decoded = (() => {
    try {
      return decodeURIComponent(selected);
    } catch {
      return selected;
    }
  })();
  return [article.article_id, article.id, article.url, article.dedupe_key, article.title, articleKey(article)]
    .filter(Boolean)
    .some((value) => String(value) === selected || String(value) === decoded);
}

function articleImage(article) {
  return pick(article.image_url, article.imageUrl, article.urlToImage, article.image, article.thumbnail, 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=900&q=70');
}

function isOpen(place) {
  const raw = pick(place.open_now, place.isOpen, place.opening_hours?.open_now, place.openingHours?.openNow);
  return raw === true || String(raw).toLowerCase() === 'true' || String(raw).toLowerCase() === 'open';
}

function useAuthState() {
  const [auth, setAuth] = useState(() => readJson(AUTH_KEY, { status: 'logged_out', user: null }));
  const save = useCallback((next) => {
    setAuth(next);
    localStorage.setItem(AUTH_KEY, JSON.stringify(next));
  }, []);
  const signIn = useCallback((user) => save({ status: 'logged_in', user }), [save]);
  const signOut = useCallback(() => save({ status: 'logged_out', user: null }), [save]);
  return { auth, signIn, signOut, isLoggedIn: auth.status === 'logged_in' && !!auth.user };
}

function useLocationState() {
  const [initialized, setInitialized] = useState(() => localStorage.getItem(LOCATION_INITIALIZED_KEY) === 'true');
  const [location, setLocation] = useState(() => {
    const saved = readJson(LOCATION_KEY, null);
    const storedCountry = localStorage.getItem(LOCATION_COUNTRY_KEY);
    const storedCity = localStorage.getItem(LOCATION_CITY_KEY);
    const storedMode = localStorage.getItem(LOCATION_MODE_KEY);
    const storedLat = Number(localStorage.getItem(LOCATION_LAT_KEY));
    const storedLng = Number(localStorage.getItem(LOCATION_LNG_KEY));
    const country = storedCountry || saved?.country || 'NZ';
    const selectedCity = storedCity || saved?.selectedCity || DEFAULT_CITY[country];
    const gps = Number.isFinite(storedLat) && Number.isFinite(storedLng) ? { lat: storedLat, lng: storedLng } : saved?.gps || null;
    return {
      country,
      placeMode: storedMode || saved?.placeMode || 'city',
      selectedCity,
      gps,
    };
  });
  const update = useCallback((patch) => {
    setLocation((current) => {
      const next = { ...current, ...patch };
      if (patch.country && patch.country !== current.country && !patch.selectedCity) {
        next.selectedCity = DEFAULT_CITY[patch.country];
      }
      if (!CITY_MAP[next.country]?.some((city) => city.slug === next.selectedCity)) {
        next.selectedCity = DEFAULT_CITY[next.country];
      }
      persistLocation(next);
      return next;
    });
  }, []);
  const markInitialized = useCallback(() => {
    localStorage.setItem(LOCATION_INITIALIZED_KEY, 'true');
    setInitialized(true);
  }, []);
  const useNearMe = useCallback(() => {
    if (!navigator.geolocation) {
      localStorage.setItem(LOCATION_PERMISSION_KEY, 'unavailable');
      update({ placeMode: 'city' });
      return Promise.resolve(false);
    }
    return new Promise((resolve) => navigator.geolocation.getCurrentPosition(
      (pos) => {
        const gps = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        const inferredCountry = inferCountryFromCoords(gps.lat, gps.lng);
        const patch = inferredCountry ? { country: inferredCountry, selectedCity: DEFAULT_CITY[inferredCountry], placeMode: 'near_me', gps } : { placeMode: 'near_me', gps };
        localStorage.setItem(LOCATION_PERMISSION_KEY, 'granted');
        update(patch);
        markInitialized();
        resolve(true);
      },
      () => {
        localStorage.setItem(LOCATION_PERMISSION_KEY, 'denied');
        update({ placeMode: 'city' });
        alert('Location is unavailable. Choose a city to explore PulsifyMe.');
        resolve(false);
      },
      { enableHighAccuracy: true, timeout: 9000 },
    ));
  }, [markInitialized, update]);
  return { location, updateLocation: update, useNearMe, locationInitialized: initialized, setLocationInitialized: markInitialized };
}

function HeaderLogo({ dark = true }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="fallback-logo">
        <span className={dark ? 'logo-white' : 'logo-green'}>Pulsify</span>
        <span className={dark ? 'logo-orange' : 'logo-orange'}>Me</span>
      </span>
    );
  }
  return <img src={LOGO} alt="PulsifyMe" className="header-logo-img" onError={() => setFailed(true)} />;
}

function ProfileButton({ auth, isLoggedIn }) {
  const navigate = useNavigate();
  if (isLoggedIn) {
    const initial = clean(auth.user?.displayName || auth.user?.email, 'P').charAt(0).toUpperCase();
    return <button className="avatar-btn" onClick={() => navigate('/my-pulse')} title="My Pulse" aria-label="My Pulse">{initial}</button>;
  }
  return (
    <button className="pulse-profile-btn" onClick={() => navigate('/signin')} title="Sign in" aria-label="Sign in">
      <img src={PULSE_ICON} alt="" />
    </button>
  );
}

function LocationControlRow({ location, updateLocation, useNearMe, home = false }) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const city = cityFor(location);
  return (
    <>
      <div className="location-row">
        <button className={`location-pill ${location.placeMode === 'near_me' ? 'active' : ''}`} onClick={useNearMe}>
          <Navigation size={14} /> Near Me
        </button>
        {home && (
          <button className="location-pill" onClick={() => setCountryOpen(true)}>
            <MapPin size={14} /> {countryName(location.country)} <ChevronDown size={14} />
          </button>
        )}
        {!home && <button className="location-pill" onClick={() => setCityOpen(true)}>
          <MapPin size={14} /> {city.name} <ChevronDown size={14} />
        </button>}
      </div>
      {countryOpen && <CountrySheet location={location} updateLocation={updateLocation} onClose={() => setCountryOpen(false)} />}
      {cityOpen && <CitySheet location={location} updateLocation={updateLocation} useNearMe={useNearMe} onClose={() => setCityOpen(false)} />}
    </>
  );
}

function CountrySheet({ location, updateLocation, onClose, onSelectComplete }) {
  const countries = [
    { code: 'NZ', title: 'New Zealand', subtitle: 'Explore local updates across Aotearoa', flag: '🇳🇿' },
    { code: 'AU', title: 'Australia', subtitle: 'Discover stories, places and events across Australia', flag: '🇦🇺' },
  ];
  return (
    <BottomSheet title="Select country" subtitle="Choose where you want to explore PulsifyMe" onClose={onClose}>
      {countries.map((item) => (
        <button key={item.code} className={`sheet-row ${location.country === item.code ? 'selected' : ''}`} onClick={() => { updateLocation({ country: item.code, selectedCity: DEFAULT_CITY[item.code], placeMode: 'city', gps: null }); onSelectComplete?.(); onClose(); }}>
          <span className="sheet-flag">{item.flag}</span>
          <span><strong>{item.title}</strong><small>{item.code === 'NZ' ? 'Aotearoa local view' : 'Australia local view'}</small></span>
          {location.country === item.code && <Check size={20} />}
        </button>
      ))}
    </BottomSheet>
  );
}

function CitySheet({ location, updateLocation, useNearMe, onClose }) {
  return (
    <BottomSheet title="Select city" subtitle={`Choose a city in ${countryName(location.country)}`} onClose={onClose}>
      <button className="sheet-row" onClick={() => { useNearMe?.(); onClose(); }}>
        <span className="sheet-flag"><Navigation size={18} /></span>
        <span><strong>Use Near Me</strong><small>Use your current location</small></span>
      </button>
      {CITY_MAP[location.country].map((city) => {
        const slug = city.slug || city.value || city.name;
        const label = city.name || city.label || city.value || 'City';
        return (
        <button key={slug} className={`sheet-row ${location.selectedCity === slug ? 'selected' : ''}`} onClick={() => { updateLocation({ selectedCity: slug, placeMode: 'city', gps: null }); onClose(); }}>
          <span className="sheet-flag">{countryFlag(location.country)}</span>
          <span><strong>{label}</strong><small>City view</small></span>
          {location.selectedCity === slug && <Check size={20} />}
        </button>
      );})}
    </BottomSheet>
  );
}

function BottomSheet({ title, subtitle, onClose, children }) {
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="bottom-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <button className="icon-btn sheet-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <div className="sheet-list">{children}</div>
      </div>
    </div>
  );
}

function LocationPermissionSheet({ onUseNearMe, onChooseManual }) {
  return (
    <div className="sheet-backdrop">
      <div className="bottom-sheet location-permission-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="sheet-handle" />
        <h2>Enable location</h2>
        <p>See stories, places, services and events around you</p>
        <button className="primary-btn" onClick={onUseNearMe}><Navigation size={17} /> Use Near Me</button>
        <button className="manual-location-btn" onClick={onChooseManual}>Choose manually</button>
      </div>
    </div>
  );
}

function AppHeader({ location, updateLocation, useNearMe, auth, isLoggedIn, title, home = false, compact = false, right, showLocation = !home }) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  return (
    <header className={`app-header ${compact ? 'compact' : ''}`}>
      <div className="header-top">
        <button className="logo-btn" onClick={() => navigate('/home')} aria-label="Home"><HeaderLogo /></button>
        {title && <span className="header-title">{title}</span>}
        <div className="header-actions">
          {right}
          {!home && showLocation && location && <LocationControlRow location={location} updateLocation={updateLocation} useNearMe={useNearMe} />}
          {home && <button className="icon-btn" title="Search" aria-label="Search" onClick={() => setSearchOpen(true)}><Search size={18} /></button>}
          {home && <button className="icon-btn" title="Notifications" aria-label="Notifications"><Bell size={18} /></button>}
          {home && <ProfileButton auth={auth} isLoggedIn={isLoggedIn} />}
        </div>
      </div>
      {home && (
        <div className="header-second">
          <button className="beta-pill" onClick={() => navigate('/beta-feedback')}>Beta Feedback</button>
          <LocationControlRow location={location} updateLocation={updateLocation} useNearMe={useNearMe} home />
        </div>
      )}
      {searchOpen && <HomeSearchOverlay onClose={() => setSearchOpen(false)} />}
    </header>
  );
}

function HomeSearchOverlay({ onClose }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const focusInput = useCallback(() => inputRef.current?.focus(), []);
  useEffect(() => {
    const id = setTimeout(focusInput, 150);
    requestAnimationFrame(focusInput);
    return () => clearTimeout(id);
  }, [focusInput]);
  const go = (path) => {
    if (!query.trim()) return;
    navigate(`${path}?q=${encodeURIComponent(query.trim())}`);
    onClose();
  };
  return (
    <div className="search-overlay" onClick={(event) => event.stopPropagation()}>
      <div className="search-overlay-row" onClick={focusInput}>
        <Search size={17} />
        <input
          ref={inputRef}
          autoFocus
          type="search"
          inputMode="search"
          enterKeyHint="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onClick={(event) => { event.stopPropagation(); focusInput(); }}
          onTouchStart={(event) => { event.stopPropagation(); }}
          placeholder="Search news, places, events or offers"
          onKeyDown={(event) => event.key === 'Enter' && go('/news')}
        />
        <button className="icon-btn" onClick={onClose} aria-label="Close search"><X size={16} /></button>
      </div>
      {!query.trim() && <p className="search-hints">Search news, places, events and offers near you.</p>}
      {query.trim() && (
        <div className="search-targets">
          <button onClick={() => go('/news')}>News</button>
          <button onClick={() => go('/nearby')}>Nearby</button>
          <button onClick={() => go('/events')}>Events</button>
          <button onClick={() => go('/home')}>Offers</button>
        </div>
      )}
    </div>
  );
}

function MarketTicker() {
  const [items, setItems] = useState(PREVIEW_TICKER);
  const load = useCallback(async () => {
    try {
      const snapshots = await Promise.all(['NZ', 'AU'].map((country) => apiGet(`/market-ticker?country=${country}`, null).catch(() => null)));
      const live = snapshots.flatMap((data) => {
        const snap = data?.snapshot || data;
        if (!snap || snap.isAvailable === false || snap.available === false) return [];
        return [{ symbol: snap.exchangeCode || snap.symbol || snap.indexCode || snap.market || 'MKT', price: Number(snap.price ?? snap.value ?? 0), change: Number(snap.change ?? snap.changeValue ?? 0) }];
      }).filter((item) => item.price);
      setItems(live.length ? live : PREVIEW_TICKER);
    } catch {
      setItems(PREVIEW_TICKER);
    }
  }, []);
  useEffect(() => {
    load();
    const id = setInterval(load, 300000);
    return () => clearInterval(id);
  }, [load]);
  const row = [...items, ...items];
  return (
    <section className="ticker-strip" aria-label="Market ticker">
      <span className="ticker-live">LIVE</span>
      <span className="ticker-divider" />
      <div className="ticker-marquee">
        <div className="ticker-track">
          {row.map((item, idx) => (
            <span className="ticker-item" key={`${item.symbol}-${idx}`}>
              <strong>{item.symbol}</strong> {formatPrice(item.price)} <em className={item.change > 0 ? 'pos' : item.change < 0 ? 'neg' : 'flat'}>{formatChange(item.change)}</em>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function formatPrice(value) {
  if (value >= 1000) return value.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value >= 10 ? value.toFixed(2) : value.toFixed(3);
}

function formatChange(value) {
  const sign = value >= 0 ? '+' : '';
  return `${sign}${Math.abs(value) >= 1 ? value.toFixed(2) : value.toFixed(3)}`;
}

function formatTimeAgo(value) {
  if (!value) return 'Latest';
  const parsed = value instanceof Date ? value : new Date(value);
  const time = parsed.getTime();
  if (!Number.isFinite(time)) return clean(value, 'Latest').replace(/T.*$/, '');
  const seconds = Math.max(0, Math.floor((Date.now() - time) / 1000));
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function getArticleTime(article) {
  return formatTimeAgo(pick(article.timeAgo, article.publishedAt, article.published_at, article.published, article.created_at));
}

function scoreBreakingArticle(article) {
  let score = 0;
  const combinedText = `${article.title || ''} ${summaryFor(article)}`.toLowerCase();
  BREAKING_KEYWORDS.forEach((kw) => {
    if (combinedText.includes(kw)) score += 10;
  });
  if (!score) return 0;

  const published = pick(article.publishedAt, article.published_at, article.published, article.created_at);
  if (published) {
    const time = new Date(published).getTime();
    if (Number.isFinite(time)) {
      const hoursAgo = (Date.now() - time) / (1000 * 60 * 60);
      if (hoursAgo > 6) return 0;
      score += Math.max(0, Math.round((6 - hoursAgo) * 5));
    }
  }

  const sourceLower = clean(article.sourceName || article.source, '').toLowerCase();
  if (TRUSTED_SOURCES.some((source) => sourceLower.includes(source))) score += 5;
  return score;
}

function findBreakingArticle(articles) {
  let best = null;
  let bestScore = 0;
  articles.forEach((article) => {
    const score = scoreBreakingArticle(article);
    if (score > bestScore) {
      bestScore = score;
      best = article;
    }
  });
  return best;
}

function BottomTabs() {
  const tabs = [
    ['/home', Home, 'Home'],
    ['/news', Newspaper, 'News'],
    ['/nearby', Store, 'Nearby'],
    ['/events', Calendar, 'Events'],
    ['/services', Wrench, 'Services'],
  ];
  return (
    <nav className="bottom-tabs" aria-label="Primary">
      {tabs.map(([to, Icon, label]) => (
        <NavLink key={to} to={to} className={({ isActive }) => `tab-link ${isActive ? 'active' : ''}`}>
          <Icon size={20} />
          <span>{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function HomeScreen({ location, updateLocation, useNearMe, auth, isLoggedIn }) {
  const navigate = useNavigate();
  const city = cityFor(location);
  const [news, setNews] = useState([]);
  const [breakingArticle, setBreakingArticle] = useState(null);
  const [ads, setAds] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const newsParams = new URLSearchParams({ country: location.country, region: location.country, city: city.name, limit: '100' });
      const newsData = await apiGet(`/news?${newsParams.toString()}`, {});
      const countryNews = normalizeCountryNews(newsData, location.country);
      setNews(countryNews.slice(0, 6));
      setBreakingArticle((prev) => {
        const next = findBreakingArticle(countryNews);
        if (!prev || !next) return next;
        const prevStillInFeed = countryNews.some((article) => articleMatchesSelected(article, articleKey(prev)));
        if (!prevStillInFeed) return next;
        return scoreBreakingArticle(next) > scoreBreakingArticle(prev) + 15 ? next : prev;
      });
    } catch {
      setNews([]);
      setBreakingArticle(null);
    } finally {
      setLoading(false);
    }
    try {
      const params = new URLSearchParams({ placement: 'HOME_OFFERS_CAROUSEL', country: location.country, city: city.name });
      if (location.gps) {
        params.set('lat', location.gps.lat);
        params.set('lng', location.gps.lng);
      }
      const adData = await apiGet(`/ads/delivery?${params.toString()}`, {});
      setAds(normalizeAds(adData));
    } catch {
      setAds([]);
    }
    try {
      const eventParams = new URLSearchParams({ region: location.country, city: city.name, lat: city.lat, lng: city.lng, radiusKm: '200', limit: '4' });
      const eventData = await apiGet(`/events?${eventParams.toString()}`, {});
      setEvents(normalizeEvents(eventData).slice(0, 3));
    } catch {
      setEvents([]);
    }
  }, [city.lat, city.lng, city.name, location.country, location.gps]);
  useEffect(() => { load(); }, [load]);
  const offers = ads.length ? ads : filterOffersForLocation(location.country, city.name);
  return (
    <>
      <AppHeader location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} home />
      <main className="screen home-screen">
        <section className="greeting-block">
          <p>{getGreeting()}</p>
          <h1>See what is happening around you</h1>
        </section>
        {breakingArticle && <BreakingNewsBar article={breakingArticle} />}
        <MarketTicker />
        <section className="welcome-panel">
          <p>{countryFlag(location.country)} {location.placeMode === 'near_me' ? `Near you · ${city.name}` : `${city.name} · ${countryName(location.country)}`}</p>
          <h1>See what is happening around you</h1>
        </section>
        <OffersCarousel offers={offers} />
        <section className="section-block">
          <div className="section-heading">
            <div>
              <h2>Trending News</h2>
            </div>
            <button className="text-pill" onClick={() => navigate('/news')}>More News</button>
          </div>
          {loading ? <FriendlyState title="Refreshing local feed" /> : <TrendingHero articles={news.slice(0, 6)} />}
          {false && news.slice(0, 4).map((article) => (
            <button className="trend-card" key={articleKey(article)} onClick={() => navigate(`/news?articleId=${encodeURIComponent(articleKey(article))}`)}>
              <img src={articleImage(article)} alt="" />
              <span>
                <strong>{clean(article.title, 'Local update')}</strong>
                <small>{clean(article.sourceName || article.source, 'PulsifyMe')} · {clean(article.regionTag || article.region || location.country)}</small>
              </span>
            </button>
          ))}
        </section>
        <QuickCards />
        <EventsPreview events={events} />
      </main>
    </>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function TrendingHero({ articles }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (articles.length < 2) return undefined;
    const id = setInterval(() => setIdx((current) => (current + 1) % articles.length), 4500);
    return () => clearInterval(id);
  }, [articles.length]);
  if (!articles.length) return <FriendlyState title="No trending stories yet" body="Try refreshing again shortly." />;
  const article = articles[idx % articles.length];
  return (
    <>
      <button className="trending-hero" onClick={() => navigate(`/news?articleId=${encodeURIComponent(articleKey(article))}`)}>
        <img src={articleImage(article)} alt="" />
        <span className="trending-overlay">
          <small>{clean(article.sourceName || article.source, 'PulsifyMe')} · {getArticleTime(article)}</small>
          <strong>{clean(article.title, 'Local update')}</strong>
        </span>
      </button>
      <div className="dots">{articles.map((_, i) => <button key={i} className={i === idx ? 'active' : ''} onClick={() => setIdx(i)} aria-label={`Story ${i + 1}`} />)}</div>
    </>
  );
}

function BreakingNewsBar({ article }) {
  const navigate = useNavigate();
  return (
    <button className="breaking-news-bar" onClick={() => navigate(`/news?articleId=${encodeURIComponent(articleKey(article))}`)}>
      <span className="breaking-label">BREAKING</span>
      <strong>{clean(article.shortTitle || article.title, 'Breaking news')}</strong>
      <ChevronRight size={16} />
    </button>
  );
}

function QuickCards() {
  return (
    <section className="quick-card-grid">
      <NavLink to="/nearby" className="quick-card"><Store size={19} /><span><strong>Nearby</strong><small>Places & cafes</small></span></NavLink>
      <NavLink to="/services" className="quick-card"><Wrench size={19} /><span><strong>Services</strong><small>Local help</small></span></NavLink>
    </section>
  );
}

function EventsPreview({ events }) {
  if (!events.length) return null;
  return (
    <section className="section-block events-preview">
      <div className="section-heading compact"><h2>Events</h2><NavLink className="text-pill" to="/events">See All</NavLink></div>
      <div className="home-events-row">
      {events.slice(0, 2).map((event, idx) => (
        <NavLink className="home-event-card" to="/events" key={event.id || idx}>
          <img src={pick(event.image_url, event.imageUrl, event.image, 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=600&q=70')} alt="" />
          <span className="event-chip">{clean(event.category, 'Event')}</span>
          <strong>{clean(event.title || event.name, 'Local event')}</strong>
          <small>{formatTimeAgo(pick(event.start_date_time, event.startAt, event.datetime_start, event.date))}</small>
          <em>{clean(event.venue || event.location || event.address, 'Location TBC')}</em>
        </NavLink>
      ))}
      </div>
    </section>
  );
}

function OffersCarousel({ offers }) {
  const [idx, setIdx] = useState(0);
  const offer = offers[idx % Math.max(offers.length, 1)];
  if (!offer) return null;
  const handleClick = () => {
    if (offer.ad_id || offer.id) {
      const adId = offer.ad_id || offer.id;
      if (offer.ad_id) fetch(apiUrl(`/ads/${adId}/click`), { method: 'POST' }).catch(() => {});
    }
    if (offer.destination_url) window.open(offer.destination_url, '_blank', 'noopener,noreferrer');
    else alert(`${offer.title}\n\n${offer.offer_details || offer.description || ''}`);
  };
  return (
    <section className="offers-block">
      <div className="section-heading compact"><h2><Tag size={16} /> Offers</h2></div>
      <button className="offer-card" onClick={handleClick}>
        <img src={offer.image || offer.image_url} alt="" />
        <span className="offer-copy">
          <small>{offer.merchant_name || offer.merchantName || 'Local offer'}</small>
          <strong>{offer.title}</strong>
          <em>{offer.cta_text || 'View offer'}</em>
        </span>
      </button>
      {offers.length > 1 && <div className="dots">{offers.map((_, i) => <button key={i} className={i === idx ? 'active' : ''} onClick={() => setIdx(i)} aria-label={`Offer ${i + 1}`} />)}</div>}
    </section>
  );
}

function normalizeAds(data) {
  if (Array.isArray(data)) return data;
  return data?.ads || data?.items || data?.results || [];
}

function filterOffersForLocation(country, cityName) {
  const citySlug = cityName.toLowerCase().replace(/\s+/g, '-');
  return DUMMY_OFFERS.filter((offer) => offer.is_active && offer.country === country && (!offer.city || offer.city === citySlug));
}

function NewsScreen({ location, updateLocation, useNearMe, auth, isLoggedIn }) {
  const [searchParams] = useSearchParams();
  const city = cityFor(location);
  const [category, setCategory] = useState('');
  const [news, setNews] = useState([]);
  const [activeNewsIndex, setActiveNewsIndex] = useState(0);
  const [newsViewportSize, setNewsViewportSize] = useState({ height: 0, width: 0 });
  const [summaryFitClass, setSummaryFitClass] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(() => new Set(readJson(SAVED_NEWS_KEY, [])));
  const [speakingId, setSpeakingId] = useState('');
  const [reportArticle, setReportArticle] = useState(null);
  const newsViewportRef = useRef(null);
  const newsPagerRef = useRef(null);
  const activeNewsIndexRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchStartTimeRef = useRef(0);
  const isNewsPagingRef = useRef(false);
  const newsPagingTimerRef = useRef(null);
  const selectedArticleId = searchParams.get('articleId');
  const clampNewsIndex = useCallback((index) => {
    if (!news.length) return 0;
    return Math.max(0, Math.min(news.length - 1, index));
  }, [news.length]);
  const releaseNewsPagingLock = useCallback(() => {
    window.clearTimeout(newsPagingTimerRef.current);
    newsPagingTimerRef.current = window.setTimeout(() => {
      isNewsPagingRef.current = false;
    }, 420);
  }, []);
  const goToNewsIndex = useCallback((index, behavior = 'smooth') => {
    if (!news.length) return;
    const nextIndex = clampNewsIndex(index);
    const pager = newsPagerRef.current;
    isNewsPagingRef.current = true;
    activeNewsIndexRef.current = nextIndex;
    setActiveNewsIndex(nextIndex);
    if (pager) {
      pager.scrollTo({ top: nextIndex * pager.clientHeight, behavior });
    }
    releaseNewsPagingLock();
  }, [clampNewsIndex, news.length, releaseNewsPagingLock]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ country: location.country, region: location.country, city: city.name, limit: '50' });
      if (category) params.set('category', category);
      const data = await apiGet(`/news?${params.toString()}`, {});
      setNews(normalizeCountryNews(data, location.country).slice(0, 50));
    } catch {
      setNews([]);
    } finally {
      setLoading(false);
    }
  }, [category, city.name, location.country]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    activeNewsIndexRef.current = activeNewsIndex;
  }, [activeNewsIndex]);
  useEffect(() => {
    if (!news.length) {
      activeNewsIndexRef.current = 0;
      setActiveNewsIndex(0);
      return;
    }
    const selectedIndex = selectedArticleId ? news.findIndex((article) => articleMatchesSelected(article, selectedArticleId)) : -1;
    window.requestAnimationFrame(() => goToNewsIndex(selectedIndex >= 0 ? selectedIndex : 0, 'auto'));
  }, [goToNewsIndex, news, selectedArticleId]);
  useEffect(() => {
    const pager = newsPagerRef.current;
    if (!pager || !news.length || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (isNewsPagingRef.current) return;
      const visible = entries
        .filter((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.6)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible) return;
      const nextIndex = Number(visible.target.dataset.newsIndex);
      if (Number.isFinite(nextIndex) && nextIndex !== activeNewsIndexRef.current) {
        activeNewsIndexRef.current = nextIndex;
        setActiveNewsIndex(nextIndex);
      }
    }, { root: pager, threshold: [0.6, 0.7] });
    pager.querySelectorAll('.pwa-news-page').forEach((page) => observer.observe(page));
    return () => observer.disconnect();
  }, [news]);
  useEffect(() => () => {
    window.speechSynthesis?.cancel();
    window.clearTimeout(newsPagingTimerRef.current);
  }, []);
  const handleCategoryChange = (value) => {
    setCategory(value);
    goToNewsIndex(0, 'auto');
  };
  const handleNewsTouchStart = (event) => {
    touchStartYRef.current = event.touches[0]?.clientY || 0;
    touchStartTimeRef.current = Date.now();
  };
  const handleNewsTouchEnd = (event) => {
    if (isNewsPagingRef.current) return;
    const endY = event.changedTouches[0]?.clientY || touchStartYRef.current;
    const deltaY = endY - touchStartYRef.current;
    const elapsed = Date.now() - touchStartTimeRef.current;
    if (elapsed < 40) return;
    if (Math.abs(deltaY) < 48) return;
    const direction = deltaY > 0 ? -1 : 1;
    goToNewsIndex(activeNewsIndexRef.current + direction);
  };
  const handleNewsWheel = (event) => {
    if (Math.abs(event.deltaY) < 18) return;
    event.preventDefault();
    if (isNewsPagingRef.current) return;
    goToNewsIndex(activeNewsIndexRef.current + (event.deltaY > 0 ? 1 : -1));
  };
  const saveSaved = (next) => {
    setSaved(next);
    localStorage.setItem(SAVED_NEWS_KEY, JSON.stringify([...next]));
  };
  const toggleSpeak = (article) => {
    const id = articleKey(article);
    if (speakingId === id) {
      window.speechSynthesis?.cancel();
      setSpeakingId('');
      return;
    }
    window.speechSynthesis?.cancel();
    const utterance = new SpeechSynthesisUtterance(`${article.title}. ${summaryFor(article)}`);
    utterance.onend = () => setSpeakingId('');
    setSpeakingId(id);
    window.speechSynthesis?.speak(utterance);
  };
  useEffect(() => {
    const updateViewportHeight = () => {
      const h = window.visualViewport?.height || window.innerHeight;
      if (h) document.documentElement.style.setProperty('--app-vh', `${h}px`);
    };

    updateViewportHeight();
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);

    return () => {
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);
  useLayoutEffect(() => {
    const update = () => {
      const el = newsViewportRef.current;
      if (!el) return;
      setNewsViewportSize({ height: el.clientHeight, width: el.clientWidth });
    };

    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer && newsViewportRef.current) observer.observe(newsViewportRef.current);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);
  useLayoutEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const page = newsPagerRef.current?.querySelector(`.pwa-news-page[data-news-index="${activeNewsIndex}"]`);
      const card = page?.querySelector('.pwa-news-card');
      const summary = page?.querySelector('.pwa-news-summary');
      const text = page?.querySelector('.pwa-news-summary-text');
      if (!card || !summary || !text) return;
      const overflow = text.scrollHeight - summary.clientHeight;
      const next = overflow > 14 ? 'summary-ultra-tight' : overflow > 2 ? 'summary-tight' : '';
      if (process.env.NODE_ENV !== 'production' && overflow > 2) {
        // Helps catch PWA card-fit regressions without changing the user-visible layout.
        console.warn('[PWA News] Summary clipped', {
          activeIndex: activeNewsIndex,
          summaryHeight: text.scrollHeight,
          boxHeight: summary.clientHeight,
        });
      }
      setSummaryFitClass((current) => (current[activeNewsIndex] === next ? current : { ...current, [activeNewsIndex]: next }));
    });
    return () => window.cancelAnimationFrame(id);
  }, [activeNewsIndex, news, newsViewportSize]);
  const hasCouncil = news.some((a) => String(a.source_type || a.sourceType || '').toLowerCase() === 'council');
  return (
    <div className="pwa-news-screen">
      <div className="pwa-news-header">
        <AppHeader
          location={location}
          updateLocation={updateLocation}
          useNearMe={useNearMe}
          auth={auth}
          isLoggedIn={isLoggedIn}
          compact
          showLocation={false}
          right={<><span className="counter-pill">{news.length ? `${clampNewsIndex(activeNewsIndex) + 1} / ${news.length}` : '0 / 0'}</span>{news.length > 8 && <span className="header-pill">More News</span>}{hasCouncil && <span className="header-pill">Council</span>}<button className="icon-btn" onClick={load} aria-label="Refresh"><RefreshCw size={17} /></button></>}
        />
      </div>
      <div className="category-strip pwa-news-categories">
        {NEWS_CATEGORIES.map((item) => <button key={item.label} className={category === item.value ? 'active' : ''} onClick={() => handleCategoryChange(item.value)}>{item.label}</button>)}
      </div>
      <div className="pwa-news-viewport" ref={newsViewportRef}>
        <main className="pwa-news-pager" ref={newsPagerRef} onTouchStart={handleNewsTouchStart} onTouchEnd={handleNewsTouchEnd} onWheel={handleNewsWheel}>
          {loading && <FriendlyState title="Refreshing your local feed" />}
          {!loading && !news.length && <FriendlyState title="No stories yet" body="Try another category or refresh shortly." action={load} />}
          {news.map((article, idx) => {
            const rawSummary = cleanPwaNewsSummary(pick(article.summary, article.description, article.ai_summary, article.content));
            const layout = getPwaNewsLayoutConfig({
              cardHeight: newsViewportSize.height,
              cardWidth: newsViewportSize.width,
              summary: rawSummary,
            });
            const backendWordCount = Number(pick(article.summary_word_count, article.summaryWordCount, article.summaryWordCountEstimate, 0));
            const displaySummary = backendWordCount > 0 && backendWordCount <= 90
              ? cleanPwaNewsSummary(rawSummary)
              : safeTruncatePwaSummary(rawSummary, 90);
            const heroHeight = Math.round((newsViewportSize.height || 680) * layout.imageRatio);
            const cardClass = ['pwa-news-card', layout.bucket, summaryFitClass[idx]].filter(Boolean).join(' ');
            return (
            <article className="pwa-news-page" data-news-index={idx} key={articleKey(article)}>
              <div
                className={cardClass}
                style={{
                  '--news-hero-h': `${heroHeight}px`,
                  '--news-headline-font': `${layout.headlineFontSize}px`,
                  '--news-summary-font': `${layout.summaryFontSize}px`,
                  '--news-summary-line': layout.summaryLineHeight,
                  '--news-action-gap': `${layout.actionGap}px`,
                  '--news-body-pad-x': `${layout.bodyPaddingX}px`,
                  '--news-cta-h': `${layout.ctaHeight}px`,
                  '--news-cta-font': `${layout.ctaFontSize}px`,
                }}
              >
                <div className="pwa-news-hero">
                  <img src={articleImage(article)} alt="" />
                  <div className="pwa-news-gradient" />
                  <span className="pwa-ai-badge"><img src={AI_ICON} alt="AI summary" /></span>
                  <span className="pwa-region-badge">{clean(article.regionTag || article.region || location.country, location.country)}</span>
                  <div className="pwa-news-over">
                    <small>{clean(article.sourceName || article.source, 'PulsifyMe')} · {getArticleTime(article)}</small>
                    <small>{clean(article.sourceName || article.source, 'PulsifyMe')} · {clean(article.timeAgo || article.publishedAt || article.published_at, 'Latest')}</small>
                    <h1 className="pwa-news-headline">{clean(article.title, 'Local update')}</h1>
                  </div>
                </div>
                <div className="pwa-news-body">
                  <div className="pwa-news-top-content">
                    <div className="pwa-news-actions">
                      <button className={speakingId === articleKey(article) ? 'active' : ''} onClick={() => toggleSpeak(article)} aria-label="Listen" title="Listen"><Volume2 size={19} /></button>
                      <button className={saved.has(articleKey(article)) ? 'active' : ''} onClick={() => { const next = new Set(saved); next.has(articleKey(article)) ? next.delete(articleKey(article)) : next.add(articleKey(article)); saveSaved(next); }} aria-label="Save" title="Save"><Bookmark size={19} fill={saved.has(articleKey(article)) ? ORANGE : 'none'} /></button>
                      <button onClick={() => shareArticle(article)} aria-label="Share" title="Share"><Share2 size={19} /></button>
                      <button onClick={() => setReportArticle(article)} aria-label="Report" title="Report"><Flag size={19} /></button>
                    </div>
                    <div className="pwa-news-summary">
                      {String(article.source_type || article.sourceType || '').toLowerCase() === 'council' && <span className="pwa-council-badge">Council</span>}
                      <p className="pwa-news-summary-text">{displaySummary}</p>
                    </div>
                  </div>
                  <div className="pwa-news-cta-zone" onTouchStart={(event) => event.stopPropagation()} onTouchEnd={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()}>
                    {pick(article.url, article.link, article.sourceUrl) ? (
                      <a className="pwa-read-full-btn" href={pick(article.url, article.link, article.sourceUrl)} target="_blank" rel="noreferrer">Read Full Article <ExternalLink size={16} /></a>
                    ) : (
                      <span className="pwa-read-full-btn disabled" aria-disabled="true">Read Full Article <ExternalLink size={16} /></span>
                    )}
                    {idx < news.length - 1 && <div className="pwa-swipe-hint">Swipe up for next</div>}
                  </div>
                </div>
              </div>
            </article>
          );})}
        </main>
      </div>
      {reportArticle && <ReportModal article={reportArticle} onClose={() => setReportArticle(null)} />}
    </div>
  );
}

function normalizeNews(data) {
  const items = Array.isArray(data) ? data : data?.articles || data?.items || data?.news || [];
  return sortNewsArticles(items.filter(Boolean));
}

function includesRegionKeyword(text, keywords) {
  return keywords.some((keyword) => {
    const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}(?=$|[^a-z0-9])`, 'i').test(text);
  });
}

function articleCountryMismatch(article, selectedCountry) {
  const structuredText = [
    article.regionTag,
    article.region,
    article.country,
    article.countryCode,
    article.location,
  ].filter(Boolean).join(' ').toLowerCase();

  const text = structuredText || [article.title].filter(Boolean).join(' ').toLowerCase();
  if (!text) return false;

  const hasNZ = includesRegionKeyword(text, NZ_REGION_KEYWORDS);
  const hasAU = includesRegionKeyword(text, AU_REGION_KEYWORDS);

  if (selectedCountry === 'NZ') return hasAU && !hasNZ;
  if (selectedCountry === 'AU') return hasNZ && !hasAU;
  return false;
}

function filterArticlesForSelectedCountry(articles, selectedCountry) {
  return articles.filter((article) => !articleCountryMismatch(article, selectedCountry));
}

function normalizeCountryNews(data, selectedCountry) {
  return filterArticlesForSelectedCountry(normalizeNews(data), selectedCountry);
}

function newsQualityRank(article) {
  const status = String(pick(article.summaryStatus, article.qualityLevel, article.qualityState, article.state, '')).toUpperCase();
  if (/(STATE_A|FULL|GOLD|HIGH|OK_FULLTEXT)/.test(status)) return 0;
  if (/(STATE_B|PARTIAL|MEDIUM|OK_PARTIAL)/.test(status)) return 1;
  if (/(STATE_C|LINK|PREVIEW|LOW|FAILED)/.test(status)) return 3;
  return 2;
}

function articleTimestamp(article) {
  const time = new Date(pick(article.publishedAt, article.published_at, article.createdAt, article.created_at, article.date, 0)).getTime();
  return Number.isFinite(time) ? time : 0;
}

function sortNewsArticles(items) {
  return [...items].sort((a, b) => {
    const quality = newsQualityRank(a) - newsQualityRank(b);
    if (quality) return quality;
    return articleTimestamp(b) - articleTimestamp(a);
  });
}

function summaryFor(article) {
  const raw = clean(pick(article.summary, article.description, article.ai_summary, article.content), 'Open the full article for more details.');
  return raw.split(/\s+/).slice(0, 70).join(' ');
}

function getPwaNewsLayoutConfig({ cardHeight, cardWidth, summary }) {
  const words = String(summary || '').trim().split(/\s+/).filter(Boolean).length;
  const narrow = cardWidth > 0 && cardWidth < 390;

  if (cardHeight < 600 || narrow) {
    return {
      bucket: 'compact',
      imageRatio: words > 75 ? 0.22 : 0.24,
      headlineFontSize: 18,
      summaryFontSize: words > 75 ? 13.8 : 14.5,
      summaryLineHeight: words > 75 ? 1.26 : 1.3,
      actionGap: 22,
      bodyPaddingX: 22,
      ctaHeight: 44,
      ctaFontSize: 15.5,
    };
  }

  if (cardHeight < 720) {
    return {
      bucket: 'small',
      imageRatio: words > 80 ? 0.24 : 0.26,
      headlineFontSize: 19,
      summaryFontSize: words > 80 ? 14.5 : 15.2,
      summaryLineHeight: words > 80 ? 1.28 : 1.32,
      actionGap: 24,
      bodyPaddingX: 24,
      ctaHeight: 46,
      ctaFontSize: 16,
    };
  }

  if (cardHeight < 840) {
    return {
      bucket: 'medium',
      imageRatio: words > 85 ? 0.27 : 0.29,
      headlineFontSize: 20,
      summaryFontSize: words > 85 ? 15.2 : 16,
      summaryLineHeight: words > 85 ? 1.3 : 1.34,
      actionGap: 26,
      bodyPaddingX: 26,
      ctaHeight: 48,
      ctaFontSize: 17,
    };
  }

  return {
    bucket: 'large',
    imageRatio: words > 90 ? 0.3 : 0.32,
    headlineFontSize: 21,
    summaryFontSize: words > 90 ? 16 : 17,
    summaryLineHeight: words > 90 ? 1.32 : 1.36,
    actionGap: 28,
    bodyPaddingX: 28,
    ctaHeight: 50,
    ctaFontSize: 18,
  };
}

function cleanPwaNewsSummary(summaryText) {
  const cleaned = String(summaryText || '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned || '';
}

function safeTruncatePwaSummary(summaryText, maxWords = 90) {
  const cleaned = cleanPwaNewsSummary(summaryText);
  if (!cleaned) return '';

  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return cleaned;

  const limited = words.slice(0, maxWords).join(' ').trim();
  const boundaries = [];
  const boundaryRegex = /[.!?](?=\s+[A-Z"'“‘]|\s*$)/g;
  let match;

  while ((match = boundaryRegex.exec(limited)) !== null) {
    const idx = match.index;
    const prev = limited[idx - 1] || '';
    const next = limited[idx + 1] || '';
    if (/\d/.test(prev) && /\d/.test(next)) continue;
    boundaries.push(idx);
  }

  const lastSafeBoundary = boundaries.length ? boundaries[boundaries.length - 1] : -1;
  if (lastSafeBoundary > 120) return limited.slice(0, lastSafeBoundary + 1).trim();
  return `${limited.replace(/[,\s]+$/, '')}...`;
}

async function shareArticle(article) {
  const url = article.url || window.location.href;
  const payload = { title: article.title, text: summaryFor(article), url };
  if (navigator.share) await navigator.share(payload).catch(() => {});
  else {
    await navigator.clipboard?.writeText(url).catch(() => {});
    alert('Article link copied.');
  }
}

function ReportModal({ article, onClose }) {
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');
  const submit = async () => {
    if (text.trim().length < 25) {
      setMessage('Please add at least 25 characters so we can understand the issue.');
      return;
    }
    try {
      await fetch(apiUrl('/report'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ article_id: articleKey(article), url: article.url, title: article.title, reason: text, platform: 'web_pwa' }) });
      setMessage('Thanks. Your report has been sent.');
      setTimeout(onClose, 900);
    } catch {
      setMessage('Report endpoint is unavailable right now. Please try again later.');
    }
  };
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <button className="icon-btn modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <h2>Report story</h2>
        <p>Tell us what looks wrong with this article.</p>
        <textarea value={text} onChange={(event) => setText(event.target.value)} rows={5} placeholder="Describe the issue..." />
        {message && <small>{message}</small>}
        <button className="primary-btn" onClick={submit}>Submit report</button>
      </div>
    </div>
  );
}

function PlacesScreen({ type, location, updateLocation, useNearMe, auth, isLoggedIn }) {
  const city = cityFor(location);
  const isServices = type === 'services';
  const cats = isServices ? SERVICE_CATEGORIES : NEARBY_CATEGORIES;
  const [category, setCategory] = useState(cats[0].value);
  const [query, setQuery] = useState('');
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const hint = SEARCH_HINTS[Math.floor(Date.now() / 5000) % SEARCH_HINTS.length];
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const selected = cats.find((item) => item.value === category) || cats[0];
      const backendCategory = isServices ? selected.value : selected.queryCategories?.[0] || selected.value;
      const params = new URLSearchParams({ mode: location.placeMode, country: location.country, city: city.name, category: backendCategory, limit: '20' });
      if (query) params.set('query', query);
      if (location.placeMode === 'near_me' && location.gps) {
        params.set('lat', location.gps.lat);
        params.set('lng', location.gps.lng);
        params.set('radiusKm', '8');
      } else {
        params.set('lat', city.lat);
        params.set('lng', city.lng);
      }
      const data = await apiGet(`/businesses?${params.toString()}`, {});
      setPlaces(normalizePlaces(data, isServices));
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [cats, category, city.lat, city.lng, city.name, isServices, location.country, location.gps, location.placeMode, query]);
  useEffect(() => { load(); }, [load]);
  const subtitle = location.placeMode === 'near_me' ? `Near you · ${city.name}` : `${city.name} · City view`;
  return (
    <>
      <AppHeader location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} compact />
      <main className="screen">
        <section className="section-title">
          <h1>{isServices ? 'Services' : 'Nearby Places'}</h1>
          <p>{subtitle}</p>
        </section>
        {!isServices && (
          <>
            <div className="search-box"><Search size={18} /><input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder={hint} /></div>
            <div className="chip-row">{SEARCH_CHIPS.map((chip) => <button key={chip} onClick={() => { setQuery(chip); setTimeout(load, 0); }}>{chip}</button>)}</div>
          </>
        )}
        <div className="category-pills">
          {cats.map((item) => <button key={item.value} className={category === item.value ? 'active' : ''} onClick={() => setCategory(item.value)}>{item.icon && <span>{item.icon}</span>}{item.label}</button>)}
        </div>
        {loading && <FriendlyState title={`Finding ${isServices ? 'services' : 'places'}`} />}
        {!loading && !places.length && <FriendlyState title="Nothing found yet" body="Try another category or city." action={load} />}
        <div className={isServices ? 'service-list' : 'places-list'}>
          {places.map((place, idx) => isServices ? <ServiceCard place={place} city={city.name} category={cats.find((c) => c.value === category)?.label || 'Service'} key={place.place_id || place.id || idx} /> : <BusinessCard place={place} category={cats.find((c) => c.value === category)} key={place.place_id || place.id || idx} />)}
        </div>
      </main>
    </>
  );
}

function normalizePlaces(data, services) {
  const items = Array.isArray(data) ? data : data?.businesses || data?.items || data?.results || [];
  if (!services) return items;
  const bad = /(restaurant|cafe|bar|hotel|park|supermarket|pharmacy)/i;
  return items.filter((item) => !bad.test(`${item.category || ''} ${item.types || ''}`));
}

function BusinessCard({ place, category }) {
  const image = pick(place.image_url, place.photoUrl, place.image, `/assets/nearby/${category?.image || 'nearby-default.jpg'}`);
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pick(place.address, place.name, ''))}`;
  return (
    <article className="business-card" onClick={() => alert(`${place.name || 'Place'}\n${place.address || ''}`)}>
      <div className="business-hero">
        <img src={image} alt="" />
        <span>{category?.label || 'Local discovery'}</span>
        <em className={isOpen(place) ? 'open' : 'closed'}>{isOpen(place) ? 'Open' : 'Closed'}</em>
        <strong>{clean(place.name, 'Local place')}</strong>
      </div>
      <div className="business-body">
        <small>Local discovery</small>
        <h3>{clean(place.name, 'Local place')}</h3>
        <p>{clean(place.address || place.vicinity, 'Address unavailable')}</p>
        <div className="meta-row"><Star size={15} fill={ORANGE} /> {clean(place.rating, '4.5')} · {clean(place.reviews || place.user_ratings_total, 'Local reviews')} · {clean(place.distance || place.distanceText, '')}</div>
        <div className="card-actions">{place.phone && <a href={`tel:${place.phone}`}><Phone size={15} /> Call</a>}<a href={maps} target="_blank" rel="noreferrer"><Navigation size={15} /> Directions</a></div>
      </div>
    </article>
  );
}

function ServiceCard({ place, city, category }) {
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pick(place.address, place.name, ''))}`;
  return (
    <article className="service-card" onClick={() => alert(`${place.name || 'Service'}\n${place.address || ''}`)}>
      <h3>{clean(place.name, 'Local service')}</h3>
      <p>{category} near {city}</p>
      <div className="meta-row"><Star size={15} fill={ORANGE} /> {clean(place.rating, '4.6')} · {clean(place.reviews || place.user_ratings_total, 'reviews')} · {clean(place.distance || place.distanceText, '')}</div>
      <small>{clean(place.address || place.vicinity, 'Address unavailable')}</small>
      {place.phone && <small>{place.phone}</small>}
      <div className="service-footer">
        <span className={`status-badge ${isOpen(place) ? 'open' : 'closed'}`}>{isOpen(place) ? 'Open' : 'Closed'}</span>
        {place.phone && <a href={`tel:${place.phone}`}><Phone size={15} /> Call</a>}
        <a href={maps} target="_blank" rel="noreferrer"><Navigation size={15} /> Directions</a>
      </div>
    </article>
  );
}

function EventsScreen({ location, updateLocation, useNearMe, auth, isLoggedIn }) {
  const city = cityFor(location);
  const [category, setCategory] = useState('All');
  const [timeFilter, setTimeFilter] = useState('All');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ region: location.country, city: city.name, lat: city.lat, lng: city.lng, radiusKm: '200', page: '1', limit: '40' });
      if (category !== 'All') params.set('category', category);
      if (timeFilter !== 'All') params.set('time_filter', timeFilter.toLowerCase().replace(/\s+/g, '_'));
      const data = await apiGet(`/events?${params.toString()}`, {});
      setEvents(normalizeEvents(data));
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [category, city.lat, city.lng, city.name, location.country, timeFilter]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      <AppHeader location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} compact />
      <main className="screen">
        <section className="section-title"><h1>Events near you</h1><p>{location.placeMode === 'near_me' ? `Near you · ${city.name}` : `${city.name} · City view`}</p></section>
        <div className="category-pills">{EVENT_CATEGORIES.map((item) => <button key={item} className={category === item ? 'active' : ''} onClick={() => setCategory(item)}>{item}</button>)}</div>
        <div className="chip-row">{EVENT_TIME_FILTERS.map((item) => <button key={item} className={timeFilter === item ? 'active' : ''} onClick={() => setTimeFilter(item)}>{item}</button>)}</div>
        {loading && <FriendlyState title="Loading events" />}
        {!loading && !events.length && <FriendlyState title="No events found" body="Try another time or category." action={load} />}
        <div className="event-list">{events.map((event, idx) => <EventCard event={event} key={event.id || idx} />)}</div>
      </main>
    </>
  );
}

function normalizeEvents(data) {
  const items = Array.isArray(data) ? data : data?.events || data?.items || data?.results || [];
  const now = Date.now();
  return items.filter((event) => new Date(pick(event.start_date_time, event.startAt, event.datetime_start, event.date, now)).getTime() >= now - 86400000).sort((a, b) => new Date(pick(a.start_date_time, a.startAt, a.date, 0)) - new Date(pick(b.start_date_time, b.startAt, b.date, 0)));
}

function EventCard({ event }) {
  const image = pick(event.image_url, event.imageUrl, event.image, 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=900&q=70');
  const date = new Date(pick(event.start_date_time, event.startAt, event.datetime_start, event.date, Date.now()));
  const maps = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pick(event.venue, event.location, event.address, event.title, ''))}`;
  return (
    <article className="event-card">
      <img src={image} alt="" />
      <div className="event-body">
        <span className="event-chip">{clean(event.category, 'Event')}</span>
        <small>{date.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {event.is_free || event.price === 0 ? 'Free' : clean(event.price, 'Tickets')}</small>
        <h3>{clean(event.title || event.name, 'Local event')}</h3>
        <p><MapPin size={14} /> {clean(event.venue || event.location || event.address, 'Location TBC')}</p>
        <div className="card-actions"><button><Calendar size={15} /> Calendar</button><a href={maps} target="_blank" rel="noreferrer"><Navigation size={15} /> Directions</a>{(event.url || event.website) && <a href={event.url || event.website} target="_blank" rel="noreferrer"><ExternalLink size={15} /> Details</a>}</div>
      </div>
    </article>
  );
}

function SignInScreen({ signIn }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const isAppleCandidate = /iPhone|iPad|Macintosh|Safari/i.test(navigator.userAgent);
  const emailSignIn = () => {
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setMessage('Please enter a valid email address.');
      return;
    }
    signIn({ id: `email_${trimmed.replace(/[^a-z0-9]/g, '_')}_${Date.now()}`, email: trimmed, displayName: trimmed.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), provider: 'email', createdAt: new Date().toISOString() });
    navigate('/home');
  };
  const configured = Boolean(process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID);
  return (
    <main className="signin-screen">
      <button className="back-link" onClick={() => navigate(-1)}><ArrowLeft size={20} /> Back</button>
      <div className="signin-logo-box"><HeaderLogo /></div>
      <p className="signin-tagline">See what is happening around you</p>
      <section className="value-box"><h2>Why sign in?</h2><p>Save articles and events, access saved items anytime, tailor your feed, and sync preferences across devices.</p></section>
      <button className="provider-btn" onClick={() => setMessage(configured ? 'Google sign-in is being configured.' : 'Google Sign-In is being configured. Please use email sign-in for now.')}>Continue with Google</button>
      {isAppleCandidate && <button className="provider-btn dark" onClick={() => setMessage('Apple Sign-In is being configured for the PWA. Please use email sign-in for now.')}>Continue with Apple</button>}
      <div className="email-box"><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" type="email" /><button onClick={emailSignIn}>Continue with Email</button></div>
      <button className="provider-btn pale" onClick={() => navigate('/beta-feedback')}>Share Beta Feedback</button>
      <button className="guest-link" onClick={() => navigate('/home')}>Continue as Guest</button>
      {message && <p className="form-message">{message}</p>}
    </main>
  );
}

function MyPulseScreen({ auth, isLoggedIn, signOut }) {
  const navigate = useNavigate();
  useEffect(() => { if (!isLoggedIn) navigate('/signin', { replace: true }); }, [isLoggedIn, navigate]);
  if (!isLoggedIn) return null;
  return (
    <main className="screen profile-screen">
      <HeaderLogo dark={false} />
      <div className="profile-card"><div className="big-avatar">{auth.user.displayName?.charAt(0) || 'P'}</div><h1>{auth.user.displayName}</h1><p>{auth.user.email}</p><small>Signed in with {auth.user.provider}</small><button className="primary-btn" onClick={() => { signOut(); navigate('/home'); }}>Sign out</button></div>
    </main>
  );
}

function BetaFeedbackScreen({ location, auth }) {
  const navigate = useNavigate();
  const textareaRef = useRef(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(() => readJson(FEEDBACK_DRAFT_KEY, {}));
  const [status, setStatus] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const current = FEEDBACK_STEPS[step];
  const isDirty = useMemo(() => Object.values(answers).some((value) => String(value || '').trim()), [answers]);
  useEffect(() => { localStorage.setItem(FEEDBACK_DRAFT_KEY, JSON.stringify(answers)); }, [answers]);
  const setAnswer = (value) => setAnswers((prev) => ({ ...prev, [current[0]]: value }));
  const focusTextarea = useCallback(() => textareaRef.current?.focus(), []);
  const requestBack = useCallback(() => {
    if (isDirty && !submitted) {
      setShowExitConfirm(true);
      return;
    }
    navigate(-1);
  }, [isDirty, navigate, submitted]);
  const exitFeedback = useCallback(() => {
    setShowExitConfirm(false);
    navigate('/home');
  }, [navigate]);
  useEffect(() => {
    if (!isDirty || submitted) return undefined;
    window.history.pushState({ pulsifymeFeedbackGuard: true }, '');
    const handlePopState = () => {
      setShowExitConfirm(true);
      window.history.pushState({ pulsifymeFeedbackGuard: true }, '');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty, submitted]);
  const startVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setStatus('Voice input is unavailable in this browser. Please type your feedback.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-NZ';
    recognition.onresult = (event) => setAnswer(`${answers[current[0]] || ''} ${event.results[0][0].transcript}`.trim());
    recognition.start();
  };
  const submit = async () => {
    const payload = {
      sessionId: CLIENT_ID,
      clientId: CLIENT_ID,
      platform: 'web_pwa',
      status: 'completed',
      country: location.country,
      city: cityFor(location).name,
      placeMode: location.placeMode,
      device: { userAgent: navigator.userAgent },
      contact: auth.user?.email || '',
      answers: FEEDBACK_STEPS.map(([stepId, title]) => ({ stepId, question: title, answerType: 'text', answer: answers[stepId] || '' })),
    };
    try {
      let res = await fetch(apiUrl('/beta-feedback'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) res = await fetch(apiUrl('/admin/beta-feedback'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error('submit failed');
      localStorage.removeItem(FEEDBACK_DRAFT_KEY);
      setSubmitted(true);
      setStatus('Thank you. Your beta feedback has been submitted.');
    } catch {
      setStatus('Feedback could not be submitted right now. Your draft is saved on this device.');
    }
  };
  return (
    <main className="feedback-screen">
      <button className="back-link" onClick={requestBack}><ArrowLeft size={20} /> Back</button>
      <p className="step-label">Step {step + 1} of {FEEDBACK_STEPS.length}</p>
      <div className="progress"><span style={{ width: `${((step + 1) / FEEDBACK_STEPS.length) * 100}%` }} /></div>
      <h1>{current[1]}</h1>
      <p>{current[2]}</p>
      <div className="feedback-textarea-shell" onClick={focusTextarea}>
        <textarea
          ref={textareaRef}
          value={answers[current[0]] || ''}
          onChange={(e) => setAnswer(e.target.value)}
          onClick={(event) => { event.stopPropagation(); focusTextarea(); }}
          onTouchStart={(event) => { event.stopPropagation(); }}
          placeholder="Type your feedback here..."
          rows={8}
        />
      </div>
      {status && <p className="form-message">{status}</p>}
      <div className="feedback-actions">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}>Back</button>
        <button onClick={startVoice}>Voice</button>
        <button onClick={() => { setAnswers({}); setStatus('Draft reset.'); }}>Reset</button>
        {step < FEEDBACK_STEPS.length - 1 ? <button className="primary-btn" onClick={() => setStep(step + 1)}>Next</button> : <button className="primary-btn" onClick={submit}>Submit</button>}
      </div>
      {showExitConfirm && (
        <div className="modal-backdrop feedback-exit-modal">
          <div className="modal-card">
            <h2>Exit feedback?</h2>
            <p>Your feedback is not submitted yet. Do you want to continue editing or exit?</p>
            <div className="confirm-actions">
              <button className="primary-btn" onClick={() => setShowExitConfirm(false)}>Continue editing</button>
              <button className="danger-btn" onClick={exitFeedback}>Exit feedback</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function FriendlyState({ title, body, action }) {
  return <div className="friendly-state"><h3>{title}</h3>{body && <p>{body}</p>}{action && <button onClick={action}>Retry</button>}</div>;
}

const PRIVACY_SUMMARY = [
  'You can use PulsifyMe by choosing a city manually. GPS is optional.',
  'PulsifyMe does not sell personal information.',
  'Some preferences, saved articles and app identifiers are stored locally on your device.',
  `You can contact ${CONTACT_EMAIL} to request access, correction or deletion of your information.`,
];

const NZ_REGION_KEYWORDS = [
  'auckland', 'wellington', 'christchurch', 'hamilton', 'tauranga',
  'dunedin', 'queenstown', 'rotorua', 'canterbury', 'otago', 'waikato',
  'bay of plenty', 'hawke', 'taranaki', 'manawatu', 'southland',
  'northland', 'marlborough', 'west coast', 'nz', 'new zealand',
];

const AU_REGION_KEYWORDS = [
  'sydney', 'melbourne', 'brisbane', 'perth', 'adelaide',
  'canberra', 'gold coast', 'newcastle', 'wollongong',
  'nsw', 'new south wales', 'vic', 'victoria', 'qld', 'queensland',
  'wa', 'western australia', 'sa', 'south australia',
  'tas', 'tasmania', 'nt', 'northern territory', 'act',
  'au', 'australia',
];

const BREAKING_KEYWORDS = [
  'earthquake', 'tsunami', 'eruption', 'volcanic', 'landslide',
  'flood', 'storm', 'cyclone', 'severe weather', 'wildfire',
  'war', 'explosion', 'emergency', 'crash', 'accident',
  'shooting', 'attack', 'fire', 'inflation', 'price surge',
  'dead', 'killed', 'missing',
];

const TRUSTED_SOURCES = [
  'rnz', 'stuff', 'nzherald', '1news', 'tvnz', 'newshub',
  'bbc', 'reuters', 'ap news', 'guardian', 'abc news',
];

const PRIVACY_SECTIONS = [
  {
    title: 'Who we are',
    body: [
      'PulsifyMe provides local news, public updates, events, nearby places, services, offers and feedback features for people in New Zealand and Australia.',
      `For privacy questions or requests, contact us at ${CONTACT_EMAIL}.`,
    ],
  },
  {
    title: 'Information we collect',
    body: [
      'We may collect the city and country you select, location permission status, approximate coordinates if you enable location, device and app information, analytics and performance logs, ad impressions and clicks, push notification tokens, optional email or sign-in details, beta feedback answers, and voice-to-text feedback transcripts if you choose to submit them.',
      `PulsifyMe also creates an anonymous client ID stored in localStorage as ${CLIENT_KEY}. This helps maintain app preferences and feedback sessions without requiring an account.`,
    ],
  },
  {
    title: 'Information stored locally',
    body: [
      'PulsifyMe stores some information in your browser or app storage, including saved articles or events, selected city and country, location mode, app preferences, sign-in state, feedback drafts and the anonymous client ID.',
      'Local storage remains on your device unless you clear browser/app data, sign out, reset a feature, or submit information to us through the app.',
    ],
  },
  {
    title: 'How we use information',
    body: [
      'We use information to show local content, personalize city and nearby experiences, deliver notifications you allow, improve reliability, understand app performance, prevent abuse, process feedback, measure offer performance and maintain product quality.',
      'We use optional sign-in information to support account-related features such as saved content and preferences.',
    ],
  },
  {
    title: 'Location data',
    body: [
      'You can manually select a city and country without using GPS. If you enable location, PulsifyMe may use approximate coordinates to show nearby places, services, events, offers and local content.',
      'You can turn off location permission at any time in your browser or device settings. If location is off, PulsifyMe can continue using your manually selected city.',
    ],
  },
  {
    title: 'Push notifications',
    body: [
      'If you opt in to push notifications, PulsifyMe may collect and use push notification tokens to send alerts about local updates or app features.',
      'You can disable push notifications at any time in your browser, device or app settings.',
    ],
  },
  {
    title: 'Feedback and voice input',
    body: [
      'If you submit beta feedback, we may collect your answers, session details, device/app information and optional contact details.',
      'If you use voice input for feedback, your browser or device may convert speech to text. PulsifyMe receives the transcript only if you submit that feedback.',
    ],
  },
  {
    title: 'Third-party services',
    body: [
      'PulsifyMe may use third-party services to provide maps, places, events, notifications, hosting, storage, analytics and content processing.',
      'These may include Google Places and Google Maps-related services, Eventfinda, News/RSS/council/public content sources, Expo notifications, AWS App Runner and DynamoDB, and Google Analytics only if enabled in production.',
    ],
  },
  {
    title: 'Advertising and offers',
    body: [
      'PulsifyMe may show advertising, promotions and local offers. We may record ad impressions, clicks, placement, country, city and approximate location context where relevant.',
      'We use this information to measure performance, prevent abuse and improve offer relevance. PulsifyMe does not sell personal information.',
    ],
  },
  {
    title: 'AI summaries and public content',
    body: [
      'PulsifyMe may summarize public news, council updates, RSS feeds, event listings and other public content using automated tools.',
      'AI summaries are provided for convenience only and may be incomplete or incorrect. You should check the original source before relying on a summary.',
    ],
  },
  {
    title: 'When we share information',
    body: [
      'We may share information with service providers who help operate PulsifyMe, such as hosting, storage, analytics, notification, mapping, event, content and infrastructure providers.',
      'We may also share information if required by law, to protect users or our service, to investigate abuse, or as part of a business transfer. We do not sell personal information.',
    ],
  },
  {
    title: 'Data storage and security',
    body: [
      'We use reasonable technical and organisational measures to protect information, including access controls and hosted infrastructure such as AWS App Runner and DynamoDB.',
      'No online service can guarantee absolute security, but we work to reduce risk and limit access to information where appropriate.',
    ],
  },
  {
    title: 'Data retention',
    body: [
      'We keep information only for as long as reasonably needed for the purposes described in this policy, unless a longer retention period is required for legal, security or operational reasons.',
      'Local data remains on your device until you clear it, reset the relevant feature or remove the app/browser data.',
    ],
  },
  {
    title: 'User choices and rights',
    body: [
      'You can choose a city manually, turn off location permission, disable push notifications, clear local storage, sign out, avoid optional feedback, and choose whether to provide an email address.',
      `You can contact ${CONTACT_EMAIL} to request access, correction or deletion of personal information we hold about you. We may need to verify your request before acting on it.`,
    ],
  },
  {
    title: 'Children',
    body: [
      'PulsifyMe is not directed to children under 13. If you believe a child has provided personal information to us, contact us so we can review and delete it where appropriate.',
    ],
  },
  {
    title: 'International processing',
    body: [
      'PulsifyMe and its service providers may process or store information in New Zealand, Australia, the United States or other countries where our providers operate.',
      'Where information is processed internationally, privacy protections may differ from those in your country.',
    ],
  },
  {
    title: 'Privacy breaches',
    body: [
      'If we become aware of a privacy breach that creates a risk of serious harm, we will assess it and take appropriate steps, which may include notifying affected users and relevant regulators where required.',
    ],
  },
  {
    title: 'Changes to policy',
    body: [
      'We may update this Privacy Policy as PulsifyMe changes. The latest version will be posted on this page with the updated date.',
    ],
  },
  {
    title: 'Contact us',
    body: [
      `For privacy questions, access requests, correction requests or deletion requests, email ${CONTACT_EMAIL}.`,
    ],
  },
];

function PrivacyPolicyPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const previousDescription = meta?.getAttribute('content');
    document.title = 'Privacy Policy | PulsifyMe';
    meta?.setAttribute('content', 'Read the PulsifyMe Privacy Policy covering location, local storage, notifications, feedback, analytics, offers, AI summaries and user choices.');
    return () => {
      document.title = previousTitle;
      if (meta && previousDescription) meta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <>
      <header className="app-header privacy-header">
        <div className="header-top">
          <Link className="logo-btn" to="/home" aria-label="PulsifyMe home"><HeaderLogo /></Link>
          <a className="privacy-contact-link" href={`mailto:${CONTACT_EMAIL}`}>Contact</a>
        </div>
      </header>
      <main className="privacy-policy-page">
        <section className="privacy-hero">
          <p>PulsifyMe</p>
          <h1>Privacy Policy</h1>
          <span>Last updated: 3 June 2026</span>
        </section>

        <section className="privacy-card privacy-summary">
          <h2>Simple summary</h2>
          {PRIVACY_SUMMARY.map((item) => <p key={item}>{item}</p>)}
        </section>

        <section className="privacy-content" aria-label="Privacy policy sections">
          {PRIVACY_SECTIONS.map((section) => (
            <article className="privacy-card" key={section.title}>
              <h2>{section.title}</h2>
              {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            </article>
          ))}
        </section>

        <section className="privacy-card privacy-contact-card">
          <h2>Contact us</h2>
          <p>Email us for privacy questions, access, correction or deletion requests.</p>
          <a className="primary-btn" href={`mailto:${CONTACT_EMAIL}`}>Email {CONTACT_EMAIL}</a>
        </section>
      </main>
    </>
  );
}

function LandingScreen() {
  return <main className="landing-screen"><HeaderLogo dark={false} /><h1>PulsifyMe</h1><p>Bite-sized local news, events, services and nearby places for NZ and AU.</p><NavLink className="primary-btn" to="/home">Open app</NavLink><NavLink className="landing-privacy-link" to="/privacy">Privacy Policy</NavLink></main>;
}

function AppShell() {
  const { auth, signIn, signOut, isLoggedIn } = useAuthState();
  const { location, updateLocation, useNearMe, locationInitialized, setLocationInitialized } = useLocationState();
  const [manualLocationOpen, setManualLocationOpen] = useState(false);
  const route = useLocation();
  const privacyPaths = ['/privacy', '/privacy/', '/privacy-policy', '/privacy-policy/'];
  const isPrivacyPage = privacyPaths.includes(route.pathname);
  const showTabs = !['/signin', '/my-pulse', '/beta-feedback', '/landing', ...privacyPaths].includes(route.pathname);
  const startNearMe = async () => {
    const ok = await useNearMe();
    if (!ok) setManualLocationOpen(true);
  };
  const chooseManual = () => {
    updateLocation({ placeMode: 'city', gps: null });
    setManualLocationOpen(true);
  };
  return (
    <div className="pwa-page">
      <div className="phone-shell">
        <Routes>
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="/home" element={<HomeScreen location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} />} />
          <Route path="/news" element={<NewsScreen location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} />} />
          <Route path="/nearby" element={<PlacesScreen type="nearby" location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} />} />
          <Route path="/businesses" element={<Navigate to="/nearby" replace />} />
          <Route path="/events" element={<EventsScreen location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} />} />
          <Route path="/services" element={<PlacesScreen type="services" location={location} updateLocation={updateLocation} useNearMe={useNearMe} auth={auth} isLoggedIn={isLoggedIn} />} />
          <Route path="/signin" element={<SignInScreen signIn={signIn} />} />
          <Route path="/my-pulse" element={<MyPulseScreen auth={auth} isLoggedIn={isLoggedIn} signOut={signOut} />} />
          <Route path="/beta-feedback" element={<BetaFeedbackScreen location={location} auth={auth} />} />
          <Route path="/privacy/*" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy/*" element={<Navigate to="/privacy" replace />} />
          <Route path="/landing" element={<LandingScreen />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
        {showTabs && <BottomTabs />}
        {!isPrivacyPage && !locationInitialized && !manualLocationOpen && <LocationPermissionSheet onUseNearMe={startNearMe} onChooseManual={chooseManual} />}
        {!isPrivacyPage && !locationInitialized && manualLocationOpen && <CountrySheet location={location} updateLocation={updateLocation} onClose={() => setManualLocationOpen(false)} onSelectComplete={setLocationInitialized} />}
      </div>
    </div>
  );
}

function AppWithRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectPath = params.get('redirect');
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);
  return <AppShell />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AppWithRedirect />
    </BrowserRouter>
  );
}
